import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  DocumentTextIcon,
  DocumentArrowDownIcon,
  AcademicCapIcon,
  BookOpenIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const Resources: React.FC = () => {
  const resources = [
    {
      category: 'GST Compliance Guides',
      icon: BookOpenIcon,
      items: [
        {
          title: 'GST Compliance Checklist 2025',
          description: 'Complete monthly GST compliance checklist for CA firms and businesses',
          type: 'PDF Download',
          link: '#',
        },
        {
          title: 'GSTR-1 Filing Guide',
          description: 'Step-by-step guide to filing GSTR-1 returns accurately',
          type: 'PDF Download',
          link: '#',
        },
        {
          title: 'Input Tax Credit (ITC) Claiming Guide',
          description: 'Maximize ITC claims while staying compliant',
          type: 'PDF Download',
          link: '#',
        },
      ],
    },
    {
      category: 'Templates & Tools',
      icon: DocumentTextIcon,
      items: [
        {
          title: 'GST Invoice Template (Excel)',
          description: 'Ready-to-use GST-compliant invoice template',
          type: 'Excel Template',
          link: '#',
        },
        {
          title: 'GST Reconciliation Template',
          description: 'Reconcile purchases and sales invoices for accurate returns',
          type: 'Excel Template',
          link: '#',
        },
        {
          title: 'Client GST Data Collection Form',
          description: 'Collect all required GST information from clients',
          type: 'PDF Form',
          link: '#',
        },
      ],
    },
    {
      category: 'Video Tutorials',
      icon: AcademicCapIcon,
      items: [
        {
          title: 'AI Invoice Scanner Tutorial',
          description: 'Learn how to scan and process invoices with AI',
          type: 'Video',
          link: '#',
        },
        {
          title: 'Setting Up Client Portal',
          description: 'Configure client upload portal for your practice',
          type: 'Video',
          link: '#',
        },
        {
          title: 'Generating GST Returns',
          description: 'Auto-generate GSTR-1 and GSTR-3B from processed invoices',
          type: 'Video',
          link: '#',
        },
      ],
    },
  ];

  const blogArticles = [
    {
      title: 'Top 5 AI Tools for GST Filing in 2025',
      slug: 'top-5-ai-tools-gst-filing-2025',
      excerpt: 'Compare the best AI-powered GST software for chartered accountants. Features, pricing, and accuracy compared.',
      readTime: '8 min read',
    },
    {
      title: 'How CA Firms Save 50% Time on Invoice Processing',
      slug: 'how-ca-firms-save-time-ai-invoice-processing',
      excerpt: 'Real case studies showing how AI invoice processing saves 20+ hours per week for CA firms.',
      readTime: '6 min read',
    },
    {
      title: 'GST Automation Benefits for Chartered Accountants',
      slug: 'gst-automation-benefits-chartered-accountants',
      excerpt: 'Complete guide to GST automation benefits: time savings, accuracy, scalability, and ROI analysis.',
      readTime: '10 min read',
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Free GST Resources for CA Firms',
    description: 'Free GST guides, templates, checklists, and video tutorials for chartered accountants. Download compliance guides, invoice templates, and automation guides.',
  };

  return (
    <>
      <SEO
        title="Free GST Resources - Guides, Templates & Tools for CA Firms"
        description="Free GST compliance guides, invoice templates, checklists, and video tutorials for chartered accountants. Download GST reconciliation templates, filing guides, and automation resources."
        keywords="gst resources, gst templates, gst compliance checklist, gst invoice template, ca resources, gst filing guide"
        canonicalUrl="https://aicafriend.com/resources"
        structuredData={structuredData}
      />

      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-50 via-white to-cyan-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
            <div className="text-center">
              <div className="inline-flex items-center bg-teal-100 text-teal-800 px-4 py-2 rounded-full mb-6">
                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                <span className="font-semibold">100% Free Resources</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Free GST Resources for
                <span className="gradient-text"> CA Firms & Accountants</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Download free GST compliance guides, invoice templates, checklists, and video tutorials.
                Everything you need to streamline your GST practice.
              </p>

              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-teal-500 mr-2" />
                  No signup required
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-teal-500 mr-2" />
                  Updated for 2025
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-teal-500 mr-2" />
                  Created by CAs
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resources Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16">
              {resources.map((category, index) => (
                <div key={index}>
                  <div className="flex items-center mb-8">
                    <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
                      <category.icon className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">{category.category}</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {category.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="text-lg font-semibold text-gray-900 flex-1">{item.title}</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-teal-600 font-semibold">{item.type}</span>
                          <a
                            href={item.link}
                            className="text-teal-600 hover:text-teal-700 font-semibold text-sm flex items-center"
                          >
                            Download
                            <DocumentArrowDownIcon className="h-4 w-4 ml-1" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Articles Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Latest GST Insights & Articles
              </h2>
              <p className="text-xl text-gray-600">
                Expert guides on GST automation and practice management
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogArticles.map((article, index) => (
                <Link
                  key={index}
                  to={`/blog/${article.slug}`}
                  className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{article.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{article.excerpt}</p>
                  <div className="text-xs text-teal-600 font-semibold">{article.readTime}</div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/blog"
                className="inline-block text-teal-600 hover:text-teal-700 font-semibold"
              >
                View All Articles →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-teal-600 to-cyan-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Automate Your GST Practice?
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              These resources are just the beginning. See how AI automation can transform your entire GST workflow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/pricing"
                className="bg-white text-teal-600 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200"
              >
                Try Free for 14 Days
              </Link>
              <Link
                to="/features"
                className="border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-teal-600 transition-all duration-200"
              >
                Explore Features
              </Link>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Explore GST Automation</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/free-gst-software"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Free GST Software</h4>
                <p className="text-gray-600 text-sm">Try our AI-powered GST software for free</p>
              </Link>
              <Link
                to="/ai-invoice-processing"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">AI Invoice Processing</h4>
                <p className="text-gray-600 text-sm">Automate invoice data extraction with AI</p>
              </Link>
              <Link
                to="/case-studies"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">CA Success Stories</h4>
                <p className="text-gray-600 text-sm">See how other firms save 80% time</p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Resources;
